/**
 * StudentDetailsDialog — read-only popup showing a student's full profile.
 * Opened by clicking a student's name in the roster. Carries an "Edit details"
 * button (admin/teacher) that hands off to EditStudentDialog. A "More info"
 * toggle expands marks, attendance history and AI-tutor usage inline.
 */
import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import {
  Loader2, Pencil, User, Phone, MapPin, Cake, Hash, IdCard, TrendingUp,
  ChevronDown, CalendarCheck, GraduationCap, Sparkles,
} from "lucide-react";
import { toast } from "sonner";

const fmtDob = (d) => {
  if (!d) return "—";
  try { return new Date(d + "T00:00:00").toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }); }
  catch { return d; }
};
const fmtDate = (d) => {
  if (!d) return "—";
  try { return new Date(d).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }); }
  catch { return d; }
};

export default function StudentDetailsDialog({ open, onClose, student, canEdit, onEdit }) {
  const [full, setFull] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [insights, setInsights] = useState(null);
  const [loadingInsights, setLoadingInsights] = useState(false);

  useEffect(() => {
    if (!open || !student?.id) return;
    setFull(student);
    setExpanded(false);
    setInsights(null);
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const { data } = await api.get(`/students/${student.id}`);
        if (!cancelled) setFull({ ...student, ...data });
      } catch (e) {
        if (!cancelled) toast.error(apiErr(e.response?.data?.detail) || "Could not load student");
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [open, student?.id]);

  const toggleMore = async () => {
    const next = !expanded;
    setExpanded(next);
    if (next && !insights && student?.id) {
      setLoadingInsights(true);
      try {
        const { data } = await api.get(`/students/${student.id}/insights`);
        setInsights(data);
      } catch (e) {
        toast.error(apiErr(e.response?.data?.detail) || "Could not load history");
      } finally { setLoadingInsights(false); }
    }
  };

  const s = full || student || {};
  const apiBase = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto" data-testid="student-details-dialog">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "Outfit" }} className="flex items-center gap-2">
            <span>Student details</span>
            {loading && <Loader2 className="h-4 w-4 animate-spin text-slate-400" data-testid="details-loading" />}
          </DialogTitle>
          <DialogDescription>Profile, attendance and academic summary for this student.</DialogDescription>
        </DialogHeader>

        <div className="flex items-center gap-3 py-1">
          {s.has_photo ? (
            <img src={`${apiBase}/profile/photo/${s.id}`} className="h-14 w-14 rounded-2xl object-cover" alt={s.name} />
          ) : (
            <div className="h-14 w-14 rounded-2xl bg-sky-50 dark:bg-sky-900/40 text-sky-700 dark:text-sky-300 flex items-center justify-center text-lg font-semibold">
              {(s.name || "?").split(" ").map((p) => p[0]).slice(0, 2).join("")}
            </div>
          )}
          <div className="min-w-0">
            <div className="text-lg font-semibold text-slate-900 dark:text-slate-100 truncate" data-testid="details-name">{s.name || "—"}</div>
            <div className="text-sm text-slate-500 dark:text-slate-400">
              Class {s.class_name ?? "—"} · Section {s.section ?? "—"} · Roll #{s.roll_number ?? "—"}
            </div>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-x-4 gap-y-3 py-2">
          <Row icon={IdCard} label="Student ID" value={s.student_id || "—"} testid="details-student-id" />
          <Row icon={Hash} label="Roll number" value={s.roll_number || "—"} />
          <Row icon={User} label="Gender" value={s.gender || "—"} />
          <Row icon={Cake} label="Date of birth" value={fmtDob(s.dob)} />
          <Row icon={User} label="Parent name" value={s.parent_name || "—"} />
          <Row icon={Phone} label="Parent phone" value={s.parent_phone || "—"} />
          <Row icon={TrendingUp} label="Attendance (30d)" value={s.attendance_pct == null ? "—" : `${s.attendance_pct}%`} />
          <Row icon={MapPin} label="Address" value={s.address || "—"} full />
        </div>

        {/* More info toggle */}
        <button
          type="button"
          onClick={toggleMore}
          className="w-full flex items-center justify-between rounded-xl border border-slate-200 dark:border-slate-700 px-3 py-2.5 text-sm font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
          data-testid="details-more-info-toggle"
        >
          <span className="inline-flex items-center gap-2"><Sparkles className="h-4 w-4 text-sky-500" /> More info — marks, attendance & AI usage</span>
          <ChevronDown className={`h-4 w-4 transition-transform ${expanded ? "rotate-180" : ""}`} />
        </button>

        {expanded && (
          <div className="space-y-5 pt-1" data-testid="details-more-info-panel">
            {loadingInsights ? (
              <div className="flex items-center gap-2 text-sm text-slate-500 py-4" data-testid="insights-loading">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading history…
              </div>
            ) : insights ? (
              <>
                <MarksSection marks={insights.marks} />
                <AttendanceSection attendance={insights.attendance} />
                <AiUsageSection ai={insights.ai_usage} />
              </>
            ) : null}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="details-close-btn">Close</Button>
          {canEdit && (
            <Button onClick={() => onEdit(s)} className="bg-sky-500 hover:bg-sky-600" data-testid="details-edit-btn">
              <Pencil className="h-4 w-4 mr-1.5" /> Edit details
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SectionTitle({ icon: Icon, children }) {
  return (
    <div className="flex items-center gap-2 mb-2">
      <div className="h-7 w-7 rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 flex items-center justify-center">
        <Icon className="h-4 w-4" />
      </div>
      <div className="text-sm font-semibold text-slate-900 dark:text-slate-100" style={{ fontFamily: "Outfit" }}>{children}</div>
    </div>
  );
}

function MarksSection({ marks }) {
  return (
    <div data-testid="insights-marks">
      <SectionTitle icon={GraduationCap}>Marks history</SectionTitle>
      {(!marks || marks.length === 0) ? (
        <div className="text-sm text-slate-400 py-1">No exam results published yet.</div>
      ) : (
        <div className="space-y-2">
          {marks.map((e) => (
            <div key={e.exam_key} className="rounded-xl border border-slate-200 dark:border-slate-700 p-3" data-testid={`insights-exam-${e.exam_key}`}>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-slate-800 dark:text-slate-100">{e.label}</span>
                <span className="text-sm font-semibold text-sky-600 dark:text-sky-300">
                  {e.total}/{e.out_of}{e.percentage != null ? ` · ${e.percentage}%` : ""}
                </span>
              </div>
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                {e.subjects.map((sub, i) => (
                  <span key={i} className="text-[11px] px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300">
                    {sub.subject}: {sub.marks}/{sub.max_marks}
                  </span>
                ))}
                {(e.grade_only || []).map((g, i) => (
                  <span key={`g-${i}`} className="text-[11px] px-2 py-0.5 rounded-full bg-indigo-50 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300" title="Grade-only subject">
                    {g.subject}: {g.grade}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function AttendanceSection({ attendance }) {
  const sum = attendance?.summary || {};
  const history = attendance?.history || [];
  return (
    <div data-testid="insights-attendance">
      <SectionTitle icon={CalendarCheck}>Attendance history</SectionTitle>
      <div className="flex flex-wrap gap-2 mb-2 text-xs">
        <span className="px-2 py-1 rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300">{sum.percentage == null ? "—" : `${sum.percentage}%`} overall</span>
        <span className="px-2 py-1 rounded-lg bg-emerald-50 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300">{sum.present || 0} present</span>
        <span className="px-2 py-1 rounded-lg bg-rose-50 dark:bg-rose-900/40 text-rose-700 dark:text-rose-300">{sum.absent || 0} absent</span>
      </div>
      {history.length === 0 ? (
        <div className="text-sm text-slate-400 py-1">No attendance recorded yet.</div>
      ) : (
        <div className="max-h-40 overflow-y-auto rounded-xl border border-slate-200 dark:border-slate-700 divide-y divide-slate-100 dark:divide-slate-800">
          {history.map((d, i) => (
            <div key={i} className="flex items-center justify-between px-3 py-1.5 text-sm" data-testid={`insights-att-${i}`}>
              <span className="text-slate-600 dark:text-slate-300">{fmtDate(d.date)}{d.session ? ` · ${d.session}` : ""}</span>
              <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${d.status === "present" ? "bg-emerald-50 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300" : "bg-rose-50 dark:bg-rose-900/40 text-rose-700 dark:text-rose-300"}`}>
                {d.status}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function AiUsageSection({ ai }) {
  const a = ai || {};
  return (
    <div data-testid="insights-ai">
      <SectionTitle icon={Sparkles}>AI Tutor usage</SectionTitle>
      <div className="grid grid-cols-3 gap-2 mb-2">
        <Stat label="Time" value={`${a.total_minutes || 0}m`} />
        <Stat label="Questions" value={a.total_questions || 0} />
        <Stat label="Sessions" value={a.total_sessions || 0} />
      </div>
      {(a.by_subject || []).length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-2">
          {a.by_subject.map((b, i) => (
            <span key={i} className="text-[11px] px-2 py-0.5 rounded-full bg-sky-50 dark:bg-sky-900/40 text-sky-700 dark:text-sky-300">
              {b.subject_name}: {b.minutes}m · {b.questions}q
            </span>
          ))}
        </div>
      )}
      {(a.recent || []).length === 0 ? (
        <div className="text-sm text-slate-400 py-1">No AI Tutor sessions yet.</div>
      ) : (
        <div className="max-h-40 overflow-y-auto rounded-xl border border-slate-200 dark:border-slate-700 divide-y divide-slate-100 dark:divide-slate-800">
          {a.recent.map((r, i) => (
            <div key={i} className="flex items-center justify-between px-3 py-1.5 text-sm gap-2" data-testid={`insights-ai-session-${i}`}>
              <div className="min-w-0">
                <div className="text-slate-700 dark:text-slate-200 truncate">{r.lesson_title || r.subject_name || "Session"}</div>
                <div className="text-[11px] text-slate-400">{r.subject_name} · {fmtDate(r.started_at)}</div>
              </div>
              <span className="text-xs text-slate-500 whitespace-nowrap">{r.minutes}m · {r.question_count}q</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-700 px-3 py-2 text-center">
      <div className="text-lg font-bold text-slate-900 dark:text-slate-100" style={{ fontFamily: "Outfit" }}>{value}</div>
      <div className="text-[10px] uppercase tracking-[0.15em] text-slate-400">{label}</div>
    </div>
  );
}

function Row({ icon: Icon, label, value, full, testid }) {
  return (
    <div className={full ? "sm:col-span-2" : ""}>
      <div className="text-[10px] uppercase tracking-[0.15em] text-slate-400 dark:text-slate-500 mb-0.5 flex items-center gap-1.5">
        <Icon className="h-3 w-3" /> {label}
      </div>
      <div className="text-sm text-slate-800 dark:text-slate-100 break-words" data-testid={testid}>{value}</div>
    </div>
  );
}
